import axios from 'axios';

/**
 * Work Order Service
 * Handles all API calls related to work orders
 */
export const workOrderService = {
  /**
   * Get all work orders
   * @param {Object} params - Query parameters (filters, pagination, etc.)
   * @returns {Promise<Object>} Work orders data with pagination
   */
  async getAllWorkOrders(params = {}) {
    try {
      const response = await axios.get('/work-orders', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching work orders:', error);
      throw error;
    }
  },

  /**
   * Get all work orders matching `params`, automatically following pagination
   * until every page is loaded. Use this for views that must show ALL work
   * orders within a range (e.g. planning calendar) and where silently
   * truncating at a single page would hide data.
   *
   * @param {Object} params - Query params for `/work-orders` (e.g.
   *                          `appointment_date_from`, `appointment_date_to`,
   *                          `unplanned`, `status`, `assigned_to`, `search`,
   *                          `project_id`, `order_id`). `page` is ignored;
   *                          `limit` defaults to 200.
   * @param {Object} [options]
   * @param {number} [options.maxPages=50] - Hard safety cap to avoid
   *                                         runaway loops on a broken backend.
   * @returns {Promise<{ workOrders: Array, total: number }>}
   */
  async getAllWorkOrdersPaginated(params = {}, options = {}) {
    const maxPages = options.maxPages ?? 50;
    const limit = params.limit ?? 200;
    const baseParams = { ...params, limit };
    delete baseParams.page;

    const all = [];
    let page = 1;
    let knownTotal = null;

    try {
      while (page <= maxPages) {
        const response = await axios.get('/work-orders', {
          params: { ...baseParams, page },
        });
        const data = response.data || {};
        const items = data.workOrders || data.data || [];
        all.push(...items);

        const total =
          data.pagination?.total ??
          (typeof data.total === 'number' ? data.total : null);
        if (total != null) knownTotal = total;

        const totalPages =
          data.pagination?.total_pages ??
          (knownTotal != null ? Math.ceil(knownTotal / limit) : null);

        if (items.length === 0) break;
        if (knownTotal != null && all.length >= knownTotal) break;
        if (totalPages != null && page >= totalPages) break;
        if (knownTotal == null && items.length < limit) break;

        page += 1;
      }

      if (page > maxPages) {
        console.warn(
          `getAllWorkOrdersPaginated: hit safety cap of ${maxPages} pages ` +
            `(loaded ${all.length} of ${knownTotal ?? 'unknown'} total). ` +
            'Increase maxPages or narrow the query.'
        );
      }

      return { workOrders: all, total: knownTotal ?? all.length };
    } catch (error) {
      console.error('Error fetching paginated work orders:', error);
      throw error;
    }
  },

  /**
   * Get a single work order by ID
   * @param {string} workOrderId - Work order ID
   * @returns {Promise<Object>} Work order data
   */
  async getWorkOrderById(workOrderId) {
    try {
      const response = await axios.get(`/work-orders/${workOrderId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching work order:', error);
      throw error;
    }
  },

  /**
   * Create a new work order
   * @param {Object} workOrderData - Work order data
   * @returns {Promise<Object>} Created work order
   */
  async createWorkOrder(workOrderData) {
    try {
      const response = await axios.post('/work-orders', workOrderData);
      return response.data;
    } catch (error) {
      console.error('Error creating work order:', error);
      throw error;
    }
  },

  /**
   * Generate work order from order
   * @param {string} orderId - Order ID to generate work order from
   * @param {Object} workOrderData - Additional work order data
   * @returns {Promise<Object>} Created work order
   */
  async generateFromOrder(orderId, workOrderData = {}) {
    try {
      const response = await axios.post(`/work-orders/from-order/${orderId}`, workOrderData);
      return response.data;
    } catch (error) {
      console.error('Error generating work order from order:', error);
      throw error;
    }
  },

  /**
   * Update a work order
   * @param {string} workOrderId - Work order ID
   * @param {Object} workOrderData - Updated work order data
   * @returns {Promise<Object>} Updated work order
   */
  async updateWorkOrder(workOrderId, workOrderData) {
    try {
      const response = await axios.patch(`/work-orders/${workOrderId}`, workOrderData);
      return response.data;
    } catch (error) {
      console.error('Error updating work order:', error);
      throw error;
    }
  },

  /**
   * Update work order status
   * @param {string} workOrderId - Work order ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated work order
   */
  async updateWorkOrderStatus(workOrderId, status) {
    try {
      const response = await axios.patch(`/work-orders/${workOrderId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error('Error updating work order status:', error);
      throw error;
    }
  },

  /**
   * Upload photos for a work order
   * @param {string} workOrderId - Work order ID
   * @param {File[]} files - Array of File objects
   * @returns {Promise<{photos: Array<{id, url, original_name}>}>}
   */
  async uploadPhotos(workOrderId, files) {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('photos', file));
      const response = await axios.post(`/work-orders/${workOrderId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading work order photos:', error);
      throw error;
    }
  },

  /**
   * Complete a work order
   * @param {string} workOrderId - Work order ID
   * @param {Object} completionData - { completion_notes, signer_name, signature, photo_urls }
   * @returns {Promise<Object>} Completed work order
   */
  async completeWorkOrder(workOrderId, completionData) {
    try {
      const response = await axios.post(`/work-orders/${workOrderId}/complete`, completionData);
      return response.data;
    } catch (error) {
      console.error('Error completing work order:', error);
      throw error;
    }
  },

  /**
   * Rebuild on-site Mollie payment link + QR for a completed work order.
   * Used when the installer dismissed the completion modal and needs the QR again.
   * @param {string} workOrderId
   * @returns {Promise<{ auto_invoice: Object, payment_link: Object }>}
   */
  async getPaymentLink(workOrderId) {
    const response = await axios.post(`/work-orders/${workOrderId}/payment-link`);
    return response.data;
  },

  /**
   * Delete a work order
   * @param {string} workOrderId - Work order ID
   * @returns {Promise<void>}
   */
  async deleteWorkOrder(workOrderId) {
    try {
      await axios.delete(`/work-orders/${workOrderId}`);
    } catch (error) {
      console.error('Error deleting work order:', error);
      throw error;
    }
  },

  /**
   * Get packing slip data (digital view)
   * @param {string} workOrderId - Work order ID
   * @returns {Promise<Object>} Packing slip data
   */
  async getPackingSlip(workOrderId) {
    try {
      const response = await axios.get(`/work-orders/${workOrderId}/packing-slip`);
      return response.data;
    } catch (error) {
      console.error('Error loading packing slip:', error);
      throw error;
    }
  },

  /**
   * Combined day packing slip ("laadlijst") for the logged-in installer.
   * @param {string} date - YYYY-MM-DD (optional, defaults to today)
   * @returns {Promise<Object>} { date, work_orders: [slip...], total_items, ... }
   */
  async getMyTodayPackingSlip(date) {
    try {
      const response = await axios.get('/work-orders/my-today/packing-slip', {
        params: date ? { date } : {},
      });
      return response.data;
    } catch (error) {
      console.error('Error loading day packing slip:', error);
      throw error;
    }
  },

  /**
   * Combined day packing slip for the whole team — admin/super_admin only.
   * @param {string} date - YYYY-MM-DD (optional, defaults to today)
   * @param {string[]} userIds - optional installer filter
   */
  async getTeamTodayPackingSlip(date, userIds = []) {
    try {
      const params = date ? { date } : {};
      if (userIds.length) params.user_ids = userIds.join(',');
      const response = await axios.get('/work-orders/team-today/packing-slip', { params });
      return response.data;
    } catch (error) {
      console.error('Error loading team day packing slip:', error);
      throw error;
    }
  },

  /**
   * Team day overview — admin/super_admin only
   * @param {string} date - YYYY-MM-DD
   * @param {string[]} userIds - optional filter
   */
  async getTeamToday(date, userIds = []) {
    const params = { date };
    if (userIds.length) params.user_ids = userIds.join(',');
    const response = await axios.get('/work-orders/team-today', { params });
    return response.data;
  },

  /**
   * Team week overview — admin/super_admin only
   * @param {string} from - YYYY-MM-DD
   * @param {string} to   - YYYY-MM-DD
   * @param {string[]} userIds - optional filter
   */
  async getTeamWeek(from, to, userIds = []) {
    const params = { from, to };
    if (userIds.length) params.user_ids = userIds.join(',');
    const response = await axios.get('/work-orders/team-week', { params });
    return response.data;
  },

  /**
   * Team history overview — admin/super_admin only
   * @param {string} from - YYYY-MM-DD
   * @param {string} to   - YYYY-MM-DD
   * @param {string[]} userIds - optional filter
   */
  async getTeamHistory(from, to, userIds = []) {
    const params = { from, to };
    if (userIds.length) params.user_ids = userIds.join(',');
    const response = await axios.get('/work-orders/team-history', { params });
    return response.data;
  },

  /**
   * Download packing slip as PDF
   * @param {string} workOrderId - Work order ID
   * @returns {Promise<Blob>} PDF blob
   */
  async downloadPackingSlipPdf(workOrderId) {
    try {
      const response = await axios.get(`/work-orders/${workOrderId}/packing-slip/pdf`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading packing slip PDF:', error);
      throw error;
    }
  },

  async getMyToday(date) {
    try {
      const response = await axios.get('/work-orders/my-today', {
        params: { date },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching today work orders:', error);
      throw error;
    }
  },

  async getMyWeek(from, to) {
    try {
      const response = await axios.get('/work-orders/my-week', {
        params: { from, to },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching week work orders:', error);
      throw error;
    }
  },

  async getMyHistory(params = {}) {
    try {
      const response = await axios.get('/work-orders/my-history', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching work order history:', error);
      throw error;
    }
  },

  /**
   * Bulk update status for multiple work orders
   * @param {string[]} ids - Array of work order IDs
   * @param {string} status - New status
   * @param {Object} statusData - Additional status-specific data
   * @returns {Promise<Object>} { succeeded: [...], failed: [...] }
   */
  async bulkUpdateStatus(ids, status, statusData = {}) {
    try {
      const response = await axios.patch('/work-orders/bulk-status', {
        ids,
        status,
        ...statusData,
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk updating work order status:', error);
      throw error;
    }
  },

  async startWorkOrder(workOrderId) {
    try {
      const response = await axios.patch(`/work-orders/${workOrderId}/start`);
      return response.data;
    } catch (error) {
      console.error('Error starting work order:', error);
      throw error;
    }
  },

  /**
   * Update which project products this work order carries (its scope snapshot).
   * Rebuilds `scope_items` from the current `project.scope` for the given
   * indexes. Empty/missing `product_indexes` → all products.
   * @param {string} workOrderId - Work order ID
   * @param {number[]} productIndexes - Indexes into project.scope.products
   * @returns {Promise<Object>} Updated work order
   */
  async updateProducts(workOrderId, productIndexes = []) {
    try {
      const response = await axios.patch(`/work-orders/${workOrderId}/products`, {
        product_indexes: productIndexes,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating work order products:', error);
      throw error;
    }
  },

  /**
   * Re-sync the work order's `scope_items` snapshot from the current project
   * scope, keeping the existing product selection. Allowed in any status,
   * including frozen (in_progress+) work orders.
   * @param {string} workOrderId - Work order ID
   * @returns {Promise<Object>} Updated work order
   */
  async syncScopeItems(workOrderId) {
    try {
      const response = await axios.post(`/work-orders/${workOrderId}/sync-scope-items`);
      return response.data;
    } catch (error) {
      console.error('Error syncing work order scope items:', error);
      throw error;
    }
  },

  /**
   * Create a follow-up (carry-over) work order for the same project,
   * carrying a subset of the products that were on the original work order.
   * @param {string} workOrderId - Original work order ID
   * @param {Object} payload - { product_indexes, work_order_type?,
   *                             installation_address?, installer_notes? }
   * @returns {Promise<Object>} The new work order
   */
  async carryOver(workOrderId, payload = {}) {
    try {
      const response = await axios.post(`/work-orders/${workOrderId}/carry-over`, payload);
      return response.data;
    } catch (error) {
      console.error('Error creating carry-over work order:', error);
      throw error;
    }
  },

  /**
   * Bulk confirm tentative appointments
   * @param {Object} payload - { work_order_ids, expected_updated_at? }
   */
  async bulkConfirmWorkOrders(payload) {
    try {
      const response = await axios.post('/work-orders/bulk-confirm', payload);
      return response.data;
    } catch (error) {
      console.error('Error bulk confirming work orders:', error);
      throw error;
    }
  },

  async previewDayTechnicianPlan(payload) {
    try {
      const response = await axios.post('/planning/bulk/day-technician/preview', payload);
      return response.data;
    } catch (error) {
      console.error('Error previewing day technician plan:', error);
      throw error;
    }
  },

  async applyDayTechnicianPlan(planId) {
    try {
      const response = await axios.post('/planning/bulk/day-technician/apply', {
        plan_id: planId,
      });
      return response.data;
    } catch (error) {
      console.error('Error applying day technician plan:', error);
      throw error;
    }
  },

  async previewRouteOptimization(payload) {
    try {
      const response = await axios.post('/planning/route-optimization/preview', payload);
      return response.data;
    } catch (error) {
      console.error('Error previewing route optimization:', error);
      throw error;
    }
  },

  async applyRouteOptimization(planId) {
    try {
      const response = await axios.post('/planning/route-optimization/apply', {
        plan_id: planId,
      });
      return response.data;
    } catch (error) {
      console.error('Error applying route optimization:', error);
      throw error;
    }
  },

  async previewWeekSuggestion(payload) {
    try {
      const response = await axios.post('/planning/week-suggestion/preview', payload);
      return response.data;
    } catch (error) {
      console.error('Error previewing week suggestion:', error);
      throw error;
    }
  },

  async applyWeekSuggestion(planId) {
    try {
      const response = await axios.post('/planning/week-suggestion/apply', {
        plan_id: planId,
      });
      return response.data;
    } catch (error) {
      console.error('Error applying week suggestion:', error);
      throw error;
    }
  },

  /**
   * Place one work order on a day for a technician; the server computes the
   * slot. Rejects with a 409 ({ code: 'day_full' | 'technician_conflict' })
   * when it can't.
   * @param {string} workOrderId
   * @param {Object} payload - { date, technician_id, time?, strategy: 'append'|'at'|'exact' }
   * @returns {{ work_order, placed }}
   */
  async placeWorkOrder(workOrderId, payload) {
    const response = await axios.post(`/planning/work-orders/${workOrderId}/place`, payload);
    return response.data;
  },
};

export default workOrderService;
